import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import * as vkBotApi from '../../api/vkBot';
import AdminLayout from './AdminLayout';
import styles from './AdminVkBot.module.css';

const PERIODS = [
  { value: '7d', label: '7 дней' },
  { value: '30d', label: '30 дней' },
  { value: '90d', label: '90 дней' },
  { value: 'all', label: 'Всё время' },
];

const LEADS_PAGE_SIZE = 50;

const AdminVkBot = () => {
  const { token } = useAuth();

  const [period, setPeriod] = useState('30d');
  const [stats, setStats] = useState([]);
  const [statsError, setStatsError] = useState('');

  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [groupId, setGroupId] = useState(null);
  const [page, setPage] = useState(1);
  const [flowFilter, setFlowFilter] = useState('');
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [leadsError, setLeadsError] = useState('');

  useEffect(() => {
    setStatsError('');
    vkBotApi
      .getStats(token, period)
      .then((data) => setStats(data.stats))
      .catch((err) => setStatsError(err.response?.data?.message || 'Не удалось загрузить статистику'));
  }, [token, period]);

  const fetchLeads = useCallback(async () => {
    setLeadsLoading(true);
    setLeadsError('');
    try {
      const data = await vkBotApi.getLeads(token, { page, limit: LEADS_PAGE_SIZE, flow: flowFilter || undefined });
      setLeads(data.leads);
      setLeadsTotal(data.total);
      setGroupId(data.group_id);
    } catch (err) {
      setLeadsError(err.response?.data?.message || 'Не удалось загрузить заявки');
    } finally {
      setLeadsLoading(false);
    }
  }, [token, page, flowFilter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const totalPages = Math.max(1, Math.ceil(leadsTotal / LEADS_PAGE_SIZE));

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>ВК-бот</h1>
        </div>
        <p className={styles.description}>
          Воронки по кодовым словам в сообщениях сообщества ВК. Заявки с контактами приходят Диане в ВК и Telegram
          и сохраняются здесь.
        </p>

        {/* ---- Статистика ---- */}
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Статистика воронок</h2>
          <select className={styles.select} value={period} onChange={(e) => setPeriod(e.target.value)}>
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>

        {statsError && <div className={styles.error}>{statsError}</div>}

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Воронка</th>
                <th className={styles.th}>Кодовое слово</th>
                <th className={`${styles.th} ${styles.num}`}>Запуски</th>
                <th className={`${styles.th} ${styles.num}`}>Людей</th>
                <th className={`${styles.th} ${styles.num}`}>Клики по ссылке</th>
                <th className={`${styles.th} ${styles.num}`}>Конверсии</th>
                <th className={`${styles.th} ${styles.num}`}>«Пока не нужно»</th>
                <th className={`${styles.th} ${styles.num}`}>Заявки</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => (
                <tr key={s.flow} className={styles.row}>
                  <td className={styles.td}>{s.title}</td>
                  <td className={`${styles.td} ${styles.muted}`}>{s.keyword}</td>
                  <td className={`${styles.td} ${styles.num}`}>{s.starts}</td>
                  <td className={`${styles.td} ${styles.num}`}>{s.unique_users}</td>
                  <td className={`${styles.td} ${styles.num}`}>{s.clicks || '—'}</td>
                  <td className={`${styles.td} ${styles.num}`}>{s.conversions || '—'}</td>
                  <td className={`${styles.td} ${styles.num}`}>{s.declines || '—'}</td>
                  <td className={`${styles.td} ${styles.num} ${styles.strong}`}>{s.leads}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ---- Заявки ---- */}
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Заявки ({leadsTotal})</h2>
          <select
            className={styles.select}
            value={flowFilter}
            onChange={(e) => { setFlowFilter(e.target.value); setPage(1); }}
          >
            <option value="">Все воронки</option>
            {stats.map((s) => (
              <option key={s.flow} value={s.flow}>{s.title}</option>
            ))}
          </select>
        </div>

        {leadsError && <div className={styles.error}>{leadsError}</div>}

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Дата</th>
                <th className={styles.th}>Воронка</th>
                <th className={styles.th}>Пользователь</th>
                <th className={styles.th}>Контакты</th>
                <th className={styles.th}>Уведомления</th>
              </tr>
            </thead>
            <tbody>
              {leadsLoading ? (
                <tr><td colSpan={5} className={styles.emptyCell}>Загрузка...</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={5} className={styles.emptyCell}>Заявок пока нет</td></tr>
              ) : (
                leads.map((lead) => (
                  <tr key={lead.id} className={styles.row}>
                    <td className={`${styles.td} ${styles.nowrap}`}>{new Date(lead.created_at).toLocaleString('ru-RU')}</td>
                    <td className={styles.td}>{lead.flow_title}</td>
                    <td className={styles.td}>
                      <div className={styles.userCell}>
                        <a href={`https://vk.com/id${lead.vk_user_id}`} target="_blank" rel="noreferrer">
                          {lead.vk_name || `id${lead.vk_user_id}`}
                        </a>
                        {groupId && (
                          <a
                            className={styles.dialogLink}
                            href={`https://vk.com/gim${groupId}?sel=${lead.vk_user_id}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Открыть диалог
                          </a>
                        )}
                      </div>
                    </td>
                    <td className={`${styles.td} ${styles.contact}`}>{lead.contact_data}</td>
                    <td className={`${styles.td} ${styles.nowrap} ${styles.muted}`}>
                      ВК {lead.notified_vk ? '✅' : '❌'} · TG {lead.notified_tg ? '✅' : '❌'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <span className={styles.paginationInfo}>Страница {page} из {totalPages}</span>
            <div className={styles.paginationBtns}>
              <button className={styles.pageBtn} onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
                ← Назад
              </button>
              <button className={styles.pageBtn} onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}>
                Вперёд →
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminVkBot;
